import type { CapacityPlantState, CapacityRead, CappedRecoveryMetal, MetalPriceState, OreGradeState, RecoveryState } from '@/versions/v1/schema/capacity';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import { CAPACITY_SEED, DEFAULT_CAPACITY_PLANT_STATE, CAPACITY_DESIGN_RECOVERIES } from '@/versions/v1/shared/capacity-seed';
import { formatCurrency as formatSharedCurrency, formatCurrencyCompact as formatSharedCurrencyCompact, CURRENCY_GLYPH } from '@/versions/v1/shared/money';

export const CAPACITY_TABS = [
  { id: '0', label: 'Maximum throughput potential', disabled: false },
  { id: '1', label: 'Value-optimised operating strategy', disabled: false },
] as const;

export type CapacityTabId = (typeof CAPACITY_TABS)[number]['id'];

/** One measured capacity operating point, read through the same cursor projection as Met and Planning. */
export function capacityPlantState(state: SessionState): CapacityPlantState {
  const readings = readingsAt(state);
  const value = (id: string, fallback: number) => readings.find((reading) => reading.id === id)?.value ?? fallback;
  const pbPct = value('rd-kpi-pb-rec', DEFAULT_CAPACITY_PLANT_STATE.recoveries.pbPct);
  return {
    designRecoveries: {
      znPct: readings.find(row => row.id === 'rd-kpi-zn-rec')?.design ?? CAPACITY_DESIGN_RECOVERIES.znPct,
      pbPct: readings.find(row => row.id === 'rd-kpi-pb-rec')?.design ?? CAPACITY_DESIGN_RECOVERIES.pbPct,
      agPct: readings.find(row => row.id === 'rd-kpi-ag-rec')?.design ?? CAPACITY_DESIGN_RECOVERIES.agPct,
    },
    feedStph: value('rd-kpi-ore-td', DEFAULT_CAPACITY_PLANT_STATE.feedStph * 24) / 24,
    grade: {
      zn: value('rd-head-zn', DEFAULT_CAPACITY_PLANT_STATE.grade.zn * 100) / 100,
      pb: value('rd-head-pb', DEFAULT_CAPACITY_PLANT_STATE.grade.pb * 100) / 100,
      agOzPerShortTon: value('rd-head-ag', DEFAULT_CAPACITY_PLANT_STATE.grade.agOzPerShortTon),
      as: CAPACITY_SEED.grade.as,
    },
    recoveries: {
      znPct: value('rd-kpi-zn-rec', DEFAULT_CAPACITY_PLANT_STATE.recoveries.znPct),
      pbPct,
      agPct: value('rd-kpi-ag-rec', DEFAULT_CAPACITY_PLANT_STATE.recoveries.agPct),
    },
    concentrateGrades: {
      leadPct: value('rd-kpi-pb-grade', DEFAULT_CAPACITY_PLANT_STATE.concentrateGrades!.leadPct),
      zincPct: value('rd-kpi-zn-grade', DEFAULT_CAPACITY_PLANT_STATE.concentrateGrades!.zincPct),
    },
    zincInLeadConcentratePct: value('rd-pb-con-zn', DEFAULT_CAPACITY_PLANT_STATE.zincInLeadConcentratePct ?? 0),
    penaltyAssays: {
      leadAsPct: value('rd-spec-pb-as', DEFAULT_CAPACITY_PLANT_STATE.penaltyAssays!.leadAsPct),
      leadHgPpm: value('rd-spec-pb-hg', DEFAULT_CAPACITY_PLANT_STATE.penaltyAssays!.leadHgPpm),
      zincAsPct: value('rd-spec-zn-as', DEFAULT_CAPACITY_PLANT_STATE.penaltyAssays!.zincAsPct),
      zincPbPct: value('rd-spec-zn-pb', DEFAULT_CAPACITY_PLANT_STATE.penaltyAssays!.zincPbPct),
    },
  };
}

export function capacityTabFromSearch(value: string | null): CapacityTabId {
  return value === '1' ? '1' : '0';
}

export type HeaderKpi = {
  id: string;
  label: string;
  value: string;
  unit?: string;
  /** One comparison line, only when the figure carries a qualifier (a D23 cap). */
  sub?: string;
  tone: 'ok' | 'warn' | 'bad' | 'neutral';
};

export const RECOVERY_METAL_NAMES: Record<keyof RecoveryState, string> = {
  znPct: 'Zn',
  pbPct: 'Pb',
  agPct: 'Ag',
};

/** D23 wording: a design-basis cap reads "at design recovery"; a measured-basis cap reads "capped at X% measured". */
export function recoveryCapPhrase(cap: CappedRecoveryMetal): string {
  const name = RECOVERY_METAL_NAMES[cap.metal];
  return `${name} ${recoveryCapQualifier(cap)}`;
}

/** The cap qualifier without the metal name, for a figure already labelled with its metal. */
export function recoveryCapQualifier(cap: CappedRecoveryMetal): string {
  return cap.basis === 'design'
    ? 'at design recovery'
    : `capped at ${cap.capPct.toFixed(1)}% measured`;
}

function recoveryCapSub(metal: keyof RecoveryState, read: CapacityRead): string | undefined {
  const cap = read.scenarios[0]!.cappedRecoveryMetals.find((row) => row.metal === metal);
  return cap ? recoveryCapQualifier(cap) : undefined;
}

export function capacityHeaderKpis(read: CapacityRead): readonly HeaderKpi[] {
  const current = read.scenarios[0]!;
  const feedLimited = read.currentThroughputStph < read.measuredThroughputStph;
  const kpis: HeaderKpi[] = [
    {
      id: 'feed',
      label: feedLimited ? 'Feed limited' : `${read.sourceTags.currentFeed} feed`,
      value: read.currentThroughputStph.toFixed(1),
      unit: 'stph',
      tone: feedLimited ? 'bad' : 'neutral',
    },
    {
      id: 'zinc-recovery',
      label: 'Zn recovery',
      value: `${current.recoveries.znPct.toFixed(1)}%`,
      sub: recoveryCapSub('znPct', read),
      tone: current.recoveries.znPct >= read.recoveryFloorZnPct ? 'ok' : 'bad',
    },
    {
      id: 'lead-recovery',
      label: 'Pb recovery',
      value: `${current.recoveries.pbPct.toFixed(1)}%`,
      sub: recoveryCapSub('pbPct', read),
      tone: 'neutral',
    },
    {
      id: 'silver-recovery',
      label: 'Ag recovery',
      value: `${current.recoveries.agPct.toFixed(1)}%`,
      sub: recoveryCapSub('agPct', read),
      tone: 'neutral',
    },
    {
      id: 'ebitda',
      label: 'EBITDA / day',
      value: formatCurrency(read.scenarios[0]!.financials.ebitdaA),
      tone: 'neutral',
    },
  ];
  return kpis;
}

export type CapacityWhatIf = {
  active: boolean;
  /** "What-if · Zn 11.0%" — every input that has left the measured grade or the deck price. */
  label: string;
};

const WHAT_IF_EPSILON = 1e-9;

/**
 * A9: one what-if state on the page. Names each slider that has left the
 * measured grade or the deck price; inactive when every input is at its basis.
 */
export function capacityWhatIf(
  grade: OreGradeState,
  measured: OreGradeState,
  prices: MetalPriceState,
  deck: MetalPriceState,
): CapacityWhatIf {
  const moved: string[] = [];
  if (Math.abs(grade.zn - measured.zn) > WHAT_IF_EPSILON) moved.push(`Zn ${(grade.zn * 100).toFixed(1)}%`);
  if (Math.abs(grade.pb - measured.pb) > WHAT_IF_EPSILON) moved.push(`Pb ${(grade.pb * 100).toFixed(1)}%`);
  if (Math.abs(grade.agOzPerShortTon - measured.agOzPerShortTon) > WHAT_IF_EPSILON) moved.push(`Ag ${grade.agOzPerShortTon.toFixed(2)} oz/st`);
  if (Math.abs(grade.as - measured.as) > WHAT_IF_EPSILON) moved.push(`As ${(grade.as * 100).toFixed(3)}%`);
  if (Math.abs(prices.zincPerTonne - deck.zincPerTonne) > WHAT_IF_EPSILON) moved.push(`Zn ${CURRENCY_GLYPH}${prices.zincPerTonne.toLocaleString('en-US')}/dmt`);
  if (Math.abs(prices.leadPerTonne - deck.leadPerTonne) > WHAT_IF_EPSILON) moved.push(`Pb ${CURRENCY_GLYPH}${prices.leadPerTonne.toLocaleString('en-US')}/dmt`);
  if (Math.abs(prices.silverPerOz - deck.silverPerOz) > WHAT_IF_EPSILON) moved.push(`Ag ${CURRENCY_GLYPH}${prices.silverPerOz.toFixed(2)}/oz`);
  return {
    active: moved.length > 0,
    label: moved.length > 0 ? `What-if · ${moved.join(' · ')}` : '',
  };
}

export function formatCurrency(value: number): string {
  return formatSharedCurrency(value);
}

export function formatCurrencyCompact(value: number): string {
  return formatSharedCurrencyCompact(value);
}

export function formatSignedA(value: number): string {
  return `${value < 0 ? '−' : '+'}${formatCurrency(Math.abs(value))}`;
}
